import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import {
  type LinkedInLookupResult,
  type LinkedInMention,
  linkedInTagsService,
} from '@/services/data/LinkedInTagsService';

export type { LinkedInLookupResult, LinkedInMention };

export function useLinkedInTags(postId: string | undefined, options?: { connectionId?: string }) {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const recent = useQuery({
    queryKey: ['linkedin-recent-mentions', user?.id],
    queryFn: async () => {
      const response = await linkedInTagsService.getRecentMentions(50);
      if (response.error) throw new Error(response.error.message);
      return response.data ?? [];
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 60_000,
  });

  const lookup = useMutation<LinkedInLookupResult, Error, string>({
    mutationFn: async (url) => {
      const response = await linkedInTagsService.lookupCompany(url, options?.connectionId);
      if (response.error || !response.data) {
        throw new Error(response.error?.message ?? 'The lookup failed');
      }
      return response.data;
    },
  });

  const save = useMutation<LinkedInMention[], Error, LinkedInMention[]>({
    mutationFn: async (mentions) => {
      if (!postId) return mentions;
      const response = await linkedInTagsService.savePostMentions(postId, mentions);
      if (response.error) throw new Error(response.error.message);
      return response.data ?? mentions;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['linkedin-recent-mentions'] });
      if (postId) void queryClient.invalidateQueries({ queryKey: ['post', postId] });
    },
  });

  return {
    recent: recent.data ?? [],
    isLoadingRecent: recent.isPending,
    lookup: lookup.mutate,
    lookupResult: lookup.data ?? null,
    lookupError: lookup.error?.message ?? null,
    isLookingUp: lookup.isPending,
    resetLookup: lookup.reset,
    save: save.mutate,
    isSaving: save.isPending,
    saveError: save.error?.message ?? null,
  };
}
